import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, inject } from "@angular/core";
import { ChatContainerComponent } from "../../../ai-components/chat";
import { ChatPanelHdrComponent } from "../../../ai-components/chat/components/chat-panel-hdr/chat-panel-hdr.component";
import { AgentDefinition } from "../../../ai-components/chat/model/agent.types";
import { AiChatSessionService } from "../../../ai-components/chat/services/ai-chat-session.service";

/**
 * Property Analyst full-page chat view.
 * Renders a full-screen chat with an independently-selected agent.
 *
 * Session isolation is achieved by providing AiChatSessionService at this component level —
 * Angular DI gives it a private instance, independent from the root instance used by
 * bubble/toolbar/AI-view. All thread operations (list, switch, new chat) use the same
 * unified service logic, scoped to the agent selected here.
 */
@Component({
  selector: "app-compound-property-analyst-display",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./property-analyst-display.component.html",
  styleUrls: ["./property-analyst-display.component.scss"],
  standalone: true,
  imports: [ChatContainerComponent, ChatPanelHdrComponent],
  providers: [AiChatSessionService],
})
export class PropertyAnalystDisplayComponent {
  protected readonly chatSession = inject(AiChatSessionService);

  /** Default agent ID — initializes the session on first render. */
  @Input() set agentId(id: string) {
    if (id && !this.chatSession.agentId()) {
      this.chatSession.agentId.set(id);
    }
  }

  /** Human-readable title shown in the chat header. */
  @Input() agentLabel: string = "Property Analyst";

  /** Agent list for the header selector. */
  @Input() agents: AgentDefinition[] = [];

  /** Emitted when the user closes the chat — parent should switch display mode. */
  @Output() close = new EventEmitter<void>();

  protected readonly activeAgent = computed(() => this.agents.find((a) => a.id === this.chatSession.agentId()) ?? null);

  protected startNewChat(): void {
    this.chatSession.startNewChat();
  }

  protected onAgentChange(id: string): void {
    this.chatSession.switchAgent(id);
  }
}
